import c from 'classnames';
import { useCallback, useState } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Stripe } from 'stripe';
import Toggle from '../../common/Toggle';
import Button from '../../components/Button';
import Error from '../../components/Error';
import Info from '../../components/Info';
import IconPlusCircle from '../../components/icons/IconPlusCircle';
import { BubbleConfig, Integrations } from '../../types/settings';
import { toFriendlyName } from '../../utils/strings';
import VALIDATORS from '../../utils/validators';
import ConfigurationForm from './ConfigurationForm';

type StripeSubscriptionField = keyof Stripe.Subscription | keyof Stripe.SubscriptionItem;
const SUBSCRIPTION_FIELDS: StripeSubscriptionField[] = [
    'quantity',
    'application',
    'application_fee_percent',
    'automatic_tax',
    'billing_cycle_anchor',
    'billing_thresholds',
    'cancel_at',
    'cancel_at_period_end',
    'canceled_at',
    'collection_method',
    'created',
    'current_period_end',
    'current_period_start',
    'customer',
    'days_until_due',
    'default_payment_method',
    'default_source',
    'default_tax_rates',
    'discount',
    'ended_at',
    'latest_invoice',
    'livemode',
    'next_pending_invoice_item_invoice',
    'on_behalf_of',
    'pending_invoice_item_interval',
    'pending_setup_intent',
    'pending_update',
    'schedule',
    'start_date',
    'status',
    'transfer_data',
    'trial_end',
    'trial_start',
];

interface Props {
    configuration: Integrations;
    onSave: (configuration: Integrations) => void;
    onDelete: () => Promise<void>;
}

export default function BubbleConfiguration({ configuration, onSave, onDelete }: Props) {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const handleToggleAdvanced = useCallback(() => setShowAdvanced((prev) => !prev), []);
    const handleClickRemove = useCallback(
        (fields: { remove: (i: number) => void }, fieldIndex: number) => () =>
            window.confirm('Are you sure?') ? fields.remove(fieldIndex) : null,
        []
    );

    return (
        <ConfigurationForm<Integrations>
            initialValues={
                configuration.bubble ? configuration : { bubble: [{ appName: '', apiKey: '', syncCheckout: false }] }
            }
            name="Bubble Integration"
            onSave={onSave}
            onDelete={onDelete}
            render={({ push, values }) => (
                <>
                    <FieldArray<BubbleConfig> name="bubble">
                        {({ fields }) =>
                            fields.map((field, fieldIndex) => (
                                <div key={field} className={c('mb-8 pt-8 text-left', { 'border-t': fieldIndex > 0 })}>
                                    <div className="mb-2">
                                        <div className="flex">
                                            <label className="text-sm text-gray-600">
                                                Bubble.io App URL{' '}
                                                <a
                                                    href="https://help.pricewell.com/integrations/bubble"
                                                    className="link"
                                                >
                                                    (Documentation)
                                                </a>
                                            </label>
                                            {fieldIndex > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={handleClickRemove(fields, fieldIndex)}
                                                    title="Remove Bubble.io App"
                                                    className="h-full pl-2 place-self-center ml-auto"
                                                >
                                                    <img src="/images/close.svg" alt="" className="w-5" />
                                                </button>
                                            )}
                                        </div>
                                        <Field
                                            name={field + '.appName'}
                                            component="input"
                                            type="text"
                                            validate={VALIDATORS.validUrlOrEmpty}
                                        >
                                            {({ input, meta }) => (
                                                <>
                                                    <input
                                                        {...input}
                                                        placeholder="https://my-app.bubbleapps.io"
                                                        className="input w-full"
                                                    />
                                                    {meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={meta.error} />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Field>
                                        {(values?.bubble?.[fieldIndex]?.appName || '').endsWith('/version-test') && (
                                            <Error
                                                type="WARNING"
                                                message={
                                                    <>
                                                        This will sync LIVE Stripe transactions to your Bubble TEST
                                                        database.{' '}
                                                        <Info
                                                            info="To sync live transactions to the live database,
                                                        remove '/version-test' from the end of the URL."
                                                            className="text-gray-500"
                                                        />
                                                    </>
                                                }
                                            />
                                        )}
                                    </div>

                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Bubble.io API Key</label>
                                        <Field name={field + '.apiKey'} component="input" type="text">
                                            {({ input, meta }) => (
                                                <>
                                                    <input
                                                        {...input}
                                                        placeholder=""
                                                        className="input w-full"
                                                        autoComplete="off"
                                                    />
                                                    {meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={meta.error} />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Field>
                                    </div>

                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">
                                            Data Type <span className="text-xs">(Optional)</span>
                                        </label>
                                        <Field name={field + '.dataType'} component="input" type="text">
                                            {({ input, meta }) => (
                                                <div className="w-full">
                                                    <input
                                                        {...input}
                                                        placeholder="User"
                                                        className="input"
                                                        autoComplete="off"
                                                    />
                                                    {meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={meta.error} />
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </Field>
                                    </div>

                                    <div className="mt-2">
                                        <Field name={field + '.syncCheckout'} type="checkbox">
                                            {(props) => (
                                                <>
                                                    <label className="text-sm text-gray-600">Integration Type</label>
                                                    <div className="w-full inline-flex items-center">
                                                        <span className="mr-2 text-sm text-gray-600">
                                                            Subscription (default)
                                                        </span>
                                                        <Toggle {...props.input} checkedColour="GRAY" />
                                                        <span className="ml-2 text-sm text-gray-600">Checkout</span>
                                                    </div>
                                                </>
                                            )}
                                        </Field>
                                    </div>

                                    {!values?.bubble?.[fieldIndex].syncCheckout && (
                                        <>
                                            <div className="mt-2">
                                                <Field name={field + '.matchOnCustomerId'} type="checkbox">
                                                    {(props) => (
                                                        <>
                                                            <label className="text-sm text-gray-600">Match on</label>
                                                            <div className="w-full inline-flex items-center">
                                                                <span className="mr-2 text-sm text-gray-600">
                                                                    Email (default)
                                                                </span>
                                                                <Toggle {...props.input} checkedColour="GRAY" />
                                                                <span className="ml-2 text-sm text-gray-600">
                                                                    Stripe Customer Id
                                                                </span>
                                                            </div>
                                                        </>
                                                    )}
                                                </Field>
                                            </div>
                                            <div className="mt-1 mb-4">
                                                <button
                                                    type="button"
                                                    onClick={handleToggleAdvanced}
                                                    className="text-sm link"
                                                >
                                                    {showAdvanced ? 'Hide' : 'Show'} Advanced
                                                </button>
                                                {showAdvanced && (
                                                    <div className="flex flex-col mt-2">
                                                        <Field name={`${field}.encryptMatchId`} type="checkbox">
                                                            {(props) => (
                                                                <label className="inline-flex items-center ml-2 text-sm">
                                                                    <input
                                                                        {...props.input}
                                                                        type="checkbox"
                                                                        className="checkbox"
                                                                        defaultValue="false"
                                                                    />
                                                                    <span className="text-gray-700 text-xs ml-2">
                                                                        Match encrypted{' '}
                                                                        {values?.bubble?.[fieldIndex].matchOnCustomerId
                                                                            ? 'Stripe Customer Id'
                                                                            : 'Email'}{' '}
                                                                        (Optional)
                                                                    </span>
                                                                </label>
                                                            )}
                                                        </Field>
                                                    </div>
                                                )}
                                            </div>

                                            <FieldArray<BubbleConfig['customFields']> name={`${field}.customFields`}>
                                                {({ fields: customFields }) =>
                                                    customFields.map((customField, customFieldIndex) => (
                                                        <div className="mb-2 flex space-x-2 items-center">
                                                            <div>
                                                                <label className="text-sm text-gray-600">
                                                                    Stripe Data
                                                                </label>
                                                                <Field name={`${customField}.value`} component="select">
                                                                    {({ input, meta }) => (
                                                                        <>
                                                                            <select
                                                                                {...input}
                                                                                className="select w-full"
                                                                            >
                                                                                <option>-</option>
                                                                                {SUBSCRIPTION_FIELDS.map((field) => (
                                                                                    <option value={field}>
                                                                                        {toFriendlyName(field)}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                            {meta.error && (
                                                                                <span className="ml-2">
                                                                                    <Error message={meta.error} />
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Field>
                                                            </div>
                                                            <div className="place-self-center mt-5">→</div>
                                                            <div>
                                                                <label className="text-sm text-gray-600">
                                                                    Bubble Data
                                                                </label>
                                                                <Field
                                                                    name={`${customField}.bubbleField`}
                                                                    component="input"
                                                                    type="text"
                                                                >
                                                                    {({ input, meta }) => (
                                                                        <>
                                                                            <input
                                                                                {...input}
                                                                                placeholder="Bubble Field Name"
                                                                                className="input w-full"
                                                                                autoComplete="off"
                                                                            />
                                                                            {meta.error && (
                                                                                <span className="ml-2">
                                                                                    <Error message={meta.error} />
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Field>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={handleClickRemove(
                                                                    customFields,
                                                                    customFieldIndex
                                                                )}
                                                                title="Remove Bubble.io App"
                                                                className="h-full pl-2 place-self-center mt-5 ml-auto"
                                                            >
                                                                <img src="/images/close.svg" alt="" className="w-5" />
                                                            </button>
                                                        </div>
                                                    ))
                                                }
                                            </FieldArray>
                                        </>
                                    )}
                                    <div className="pt-4 flex">
                                        <Button
                                            type="button"
                                            kind="outline"
                                            className="ml-auto"
                                            onClick={() =>
                                                push(field + '.customFields', { bubbleField: '', value: null })
                                            }
                                        >
                                            Add Custom Field
                                        </Button>
                                    </div>
                                </div>
                            ))
                        }
                    </FieldArray>
                    <div className="pb-4 flex items-center justify-center group hover:text-black">
                        <span className="w-full relative m-5 border-b group-hover:border-black"></span>
                        <button
                            type="button"
                            onClick={() => push('bubble', { apiKey: '', appName: '' })}
                            className="mx-2"
                        >
                            <IconPlusCircle className="w-6 h-6 group-hover:text-black" />
                        </button>
                        <span className="w-full relative m-5 border-b group-hover:border-black"></span>
                    </div>
                </>
            )}
        />
    );
}
